import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface EventItem {
    label: string;
    time: string;
    align?: 'left' | 'right';
    icon?: string;
    iconPos?: 'left' | 'right';
    weddingPartyOnly?: boolean;
}


const PB_EVENTS: EventItem[] = [
    { label: "welcome_drinks", time: "20:00" }
];

const ES_EVENTS: EventItem[] = [
    { label: "siesta", time: "14:00", align: "left" },
    { label: "bus_pickup", time: "18:00", align: "right" },
    { label: "ceremony", time: "19:00", align: "left" },
    { label: "cocktail", time: "20:00", align: "right" },
    { label: "dinner", time: "21:30", align: "left" },
    { label: "band", time: "00:00", align: "right" },
    { label: "first_bus", time: "02:00", align: "left" },
    { label: "dj", time: "02:30", align: "right" },
    { label: "second_bus", time: "05:00", align: "left" },
];

export function ScheduleSection() {
    const { t, i18n } = useTranslation();
    const isSpanish = i18n.language === 'es';
    const { user } = useAuth();
    const [isWeddingParty, setIsWeddingParty] = useState(false);
    const [invitedEvents, setInvitedEvents] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchGuestData() {
            if (!user) return;
            try {
                // Check if admin
                const idTokenResult = await user.getIdTokenResult();
                const isAdmin = idTokenResult.claims.role === 'admin';

                const guestRef = doc(db, 'guests', user.uid);
                const guestSnap = await getDoc(guestRef);

                let data: { events: string[], weddingParty: boolean } = { events: [], weddingParty: false };
                if (guestSnap.exists()) {
                    data = guestSnap.data() as { events: string[], weddingParty: boolean };
                }

                if (isAdmin) {
                    setIsWeddingParty(true);
                    setInvitedEvents(['IE', 'ES', 'PB']);
                } else {
                    setIsWeddingParty(!!data.weddingParty);
                    setInvitedEvents(data.events || []);
                }
            } catch (error) {
                console.error("Error fetching guest data for schedule:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchGuestData();
    }, [user]);

    if (loading) return null;

    const showIE = invitedEvents.includes('IE');
    const showPB = invitedEvents.includes('PB');
    const showES = invitedEvents.includes('ES');

    const fridayEvents = [...PB_EVENTS];
    if (showPB && isWeddingParty) {
        fridayEvents.unshift({ label: "rehearsal", time: "18:00" });
    }

    const saturdayEvents = [...ES_EVENTS];

    return (
        <section id="schedule" className="relative w-full bg-[#DDCEA9] text-[#4A3728] overflow-hidden pt-10 pb-48">
            <div className="shell max-w-4xl mx-auto px-4">
                <div className="flex flex-col items-center gap-16">
                    {/* Header Image */}
                    <img
                        src={isSpanish ? "/images/text/horario_de_la_boda.svg" : "/images/text/the_wedding_schedule.svg"}
                        alt="The Wedding Schedule"
                        className="w-[85%] max-w-[500px] pt-16"
                        loading="lazy"
                    />

                    {/* Ireland Event */}
                    {showIE && (
                        <div className="flex flex-col items-center w-full max-w-[600px] -mt-4">
                            <div className="relative flex flex-col items-center w-full">
                                <img
                                    src={isSpanish ? "/images/text/18_DE_JULIO.svg" : "/images/text/18TH_OF_JULY.svg"}
                                    alt="Saturday, 18th of July"
                                    className="w-[75%] max-w-[360px] pt-5 relative z-20"
                                    loading="lazy"
                                />
                                <img
                                    src="/images/poc_ar_buile.svg"
                                    alt="Poc ar Buile"
                                    className="w-full max-w-[460px] -mt-4 pt-5 relative z-10"
                                    loading="lazy"
                                />
                            </div>
                            <div className="mt-8 flex flex-col items-center text-center font-sans tracking-wider text-base md:text-base opacity-80 gap-3">
                                <div>
                                    <p className="font-bold mb-2 uppercase">{t(`schedule.ie_ceremony`)}</p>
                                    <a 
                                        href={t(`schedule.ie_maps_link`)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="hover:underline underline-offset-4"
                                    >
                                        <p className="whitespace-pre-line leading-relaxed">{t(`schedule.ie_address`)}</p>
                                    </a>
                                </div>
                                <div className="mt-2">
                                    <p>From 14:00</p>
                                    <p className="mt-2 italic text-sm">{t(`schedule.ie_bus`)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Friday Event */}
                    {showPB && (
                        <div className="flex flex-col items-center w-full max-w-[600px]">
                            <div className="relative flex flex-col items-center w-full">
                                <img
                                    src={isSpanish ? "/images/text/VIERNES_28.svg" : "/images/text/FRIDAY_28TH.svg"}
                                    alt="Friday, 28th of August"
                                    className="w-[75%] max-w-[360px] pt-5 relative z-20"
                                    loading="lazy"
                                />
                                <img
                                    src="/images/nena.svg"
                                    alt="Nena"
                                    className="w-full max-w-[450px] -mt-10 relative z-10"
                                    loading="lazy"
                                />
                            </div>
                            <div className="flex flex-col items-center text-center font-sans tracking-wider text-base md:text-base opacity-80 gap-3">
                                <div>
                                    <p className="font-bold mb-1 uppercase">{t(`schedule.welcome_drinks`)}</p>
                                    <a 
                                        href={t(`schedule.pb_website`)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="hover:underline underline-offset-4 font-medium"
                                    >
                                        <p>{t(`schedule.pb_location`)}</p>
                                    </a>
                                </div>
                                <div className="mt-1">
                                    <a 
                                        href={t(`schedule.pb_maps_link`)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="hover:underline underline-offset-4 text-sm"
                                    >
                                        <p>{t(`schedule.pb_address`)}</p>
                                    </a>
                                </div>
                                <div className="mt-1">
                                    <p>20:00</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Saturday Event */}
                    {showES && (
                        <div className="flex flex-col items-center w-full max-w-[800px]">
                            {/* Spain Header */}
                            <div className="relative flex flex-col items-center w-full mb-10">
                                <img
                                    src={isSpanish ? "/images/text/SABADO_29.svg" : "/images/text/SAT_29TH.svg"}
                                    alt="Saturday, 29th of August"
                                    className="w-[80%] max-w-[400px] pt-5 relative z-20"
                                    loading="lazy"
                                />
                                <img
                                    src="/images/batanejo.svg"
                                    alt="Batanejo"
                                    className="w-full max-w-[500px] -mt-6 pt-5 relative z-10"
                                    loading="lazy"
                                />
                                <div className="mt-8 flex flex-col items-center text-center font-sans tracking-wider text-base md:text-base opacity-80 gap-3">
                                    <div>
                                        <a 
                                            href={t(`schedule.es_website`)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="hover:underline underline-offset-4 font-bold uppercase"
                                        >
                                            <p>{t(`schedule.es_location`)}</p>
                                        </a>
                                    </div>
                                    <div className="mt-1">
                                        <a 
                                            href={t(`schedule.es_maps_link`)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="hover:underline underline-offset-4 text-sm"
                                        >
                                            <p>{t(`schedule.es_address`)}</p>
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="relative w-full">
                                {/* Vertical Divider */}
                                <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-[#4A3728] opacity-30 -translate-x-1/2" />

                                <div className="flex flex-col">
                                    {saturdayEvents.map((event, idx) => (
                                        <div
                                            key={idx}
                                            className={`relative flex w-full ${event.align === 'left' ? 'justify-start' : 'justify-end'} ${idx > 0 ? '-mt-1 sm:-mt-2' : ''}`}
                                        >
                                            <div className={`w-1/2 ${event.align === 'left' ? 'pr-4 sm:pr-6 text-right' : 'pl-4 sm:pl-6 text-left'}`}>
                                                <div className="flex flex-col">
                                                    <p className="font-sans text-base md:text-base tracking-wider opacity-80">
                                                        {t(`schedule.${event.label}`)}
                                                    </p>
                                                    <p className="font-sans text-base md:text-base tracking-wider opacity-80">
                                                        {event.time}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Bus Meeting Point Map */}
                            <div className="flex flex-col items-center w-full mt-14 gap-5">
                                <div className="flex flex-col items-center gap-2">
                                    <span className="text-4xl">🚌</span>
                                    <p className="font-sans text-lg md:text-xl font-bold tracking-wider uppercase text-center">
                                        {t('schedule.bus_meeting_point_title')}
                                    </p>
                                    <p className="font-sans text-sm md:text-base tracking-wider opacity-70 text-center max-w-[400px]">
                                        {t('schedule.bus_meeting_point_desc')}
                                    </p>
                                </div>
                                <div className="w-full max-w-[500px] rounded-xl overflow-hidden shadow-lg border-2 border-[#4A3728]/20">
                                    <iframe
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1558.8!2d-1.8564551!3d38.9897917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd665fbb96ee360d%3A0x218ce80fe14be0f2!2sPunta%20del%20Parque%20Abelardo%20S%C3%A1nchez!5e0!3m2!1sen!2ses!4v1"
                                        width="100%"
                                        height="300"
                                        style={{ border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title="Bus Meeting Point - Punta del Parque Abelardo Sánchez"
                                    />
                                </div>
                                <a
                                    href="https://maps.app.goo.gl/4c3LCku9uMT2n9Hi6?g_st=iw"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A3728] text-[#DDCEA9] rounded-full font-sans text-sm tracking-wider uppercase hover:bg-[#5a4738] transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                    {t('schedule.open_in_maps')}
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
